import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { ASSETS } from "../lib/data";
import { useT } from "../lib/i18n";
import { MustacheMark } from "./MustacheMark";

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.2 } },
};
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export const Hero = () => {
  const { t } = useT();
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative h-[100svh] min-h-[640px] w-full overflow-hidden bg-black"
    >
      <div className="absolute inset-0">
        <img
          src={ASSETS.kiosk1}
          alt="Monsieur Toast counter"
          className="w-full h-full object-cover scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-transparent" />
      </div>

      <motion.div
        variants={stagger}
        initial="hidden"
        animate="show"
        className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-10 flex flex-col justify-end pb-24 md:pb-32"
      >
        <motion.div variants={fadeUp} className="mustache-divider mb-6 text-gold">
          <span className="line" />
          <MustacheMark className="h-5 w-auto" color="#D4A857" />
          <span className="line" />
          <span className="text-[11px] tracking-[0.35em] uppercase ml-3 text-gold/90">
            {t("hero.kicker")}
          </span>
        </motion.div>

        <motion.h1
          variants={fadeUp}
          data-testid="hero-headline"
          className="font-display text-white text-[44px] sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight max-w-5xl"
        >
          <span className="text-gold italic">{t("hero.headline.1")}</span>,
          <br />
          {t("hero.toast")}
        </motion.h1>

        <motion.p
          variants={fadeUp}
          data-testid="hero-subhead"
          className="mt-8 max-w-xl text-white/75 text-base md:text-lg font-light leading-relaxed"
        >
          {t("hero.subhead")}
        </motion.p>

        <motion.div variants={fadeUp} className="mt-10 flex flex-wrap items-center gap-4">
          <a
            data-testid="hero-cta-menu"
            href="#menu"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById("menu")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="inline-flex items-center gap-3 text-[12px] tracking-[0.3em] uppercase text-black bg-gold px-7 py-4 hover:bg-gold-light transition-colors"
          >
            {t("hero.cta.menu")}
          </a>
          <a
            data-testid="hero-cta-find"
            href="#find"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById("find")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="inline-flex items-center gap-3 text-[12px] tracking-[0.3em] uppercase text-gold border border-gold/40 px-7 py-4 hover:bg-gold/10 transition-colors"
          >
            {t("hero.cta.find")}
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="absolute right-6 md:right-10 bottom-10 z-10 hidden sm:flex items-center gap-3 text-white/60"
      >
        <span className="text-[10px] tracking-[0.4em] uppercase">{t("hero.scroll")}</span>
        <motion.span
          animate={{ y: [0, 6, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
        >
          <ArrowDown size={14} className="text-gold" />
        </motion.span>
      </motion.div>

      <div className="absolute top-1/2 -translate-y-1/2 left-6 md:left-10 z-10 hidden lg:block">
        <p className="font-display italic text-gold text-base tracking-wide rotate-[-90deg] origin-left whitespace-nowrap">
          {t("hero.side")} — Monsieur Toast
        </p>
      </div>
    </section>
  );
};

export default Hero;
